import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, ShieldAlert, TrendingUp, Upload, Lock, CheckCircle2, ChevronRight } from 'lucide-react';
import { PaystackPaymentButtons } from '../Payment/PaystackPaymentButtons';
interface UnlockScreenProps {
  onUnlock: () => void;
}
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};
export const UnlockScreen: React.FC<UnlockScreenProps> = ({
  onUnlock
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };
  const features = [{
    icon: AlertTriangle,
    color: 'text-red-500',
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
    title: 'Manipulation Patterns',
    description: 'Detects psychological warfare tactics'
  }, {
    icon: ShieldAlert,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    title: 'Gaslighting Detection',
    description: 'Exposes reality distortion attempts'
  }, {
    icon: TrendingUp,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    title: 'Toxicity Score',
    description: 'Calculates relationship danger level'
  }];
  return <motion.div className="w-full max-w-md mx-auto" variants={containerVariants} initial="hidden" animate="visible" data-id="element-1384">
      <motion.div className="text-center mb-8" variants={itemVariants} data-id="element-1385">
        <div className="inline-flex items-center gap-2 bg-[#FF0000]/10 text-[#FF0000] px-4 py-1.5 border border-[#FF0000]/20 mb-6 backdrop-blur-sm" data-id="element-1386">
          <span className="relative flex h-2 w-2" data-id="element-1387">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF0000] opacity-75" data-id="element-1388"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF0000]" data-id="element-1389"></span>
          </span>
          <span className="font-mono text-[10px] tracking-widest uppercase font-bold" data-id="element-1390">
            Forensic Engine Ready
          </span>
        </div>

        <h2 className="text-3xl font-bold mb-3 leading-tight text-white tracking-tight" data-id="element-1391">
          Analysis <span className="text-[#FF0000]" data-id="element-1392">Complete</span>.
        </h2>
        <p className="text-sm text-gray-400 leading-relaxed max-w-xs mx-auto font-mono" data-id="element-1393">
          Initialize forensic scan on your conversation history to detect hidden
          patterns.
        </p>
      </motion.div>

      <motion.div className="mb-8 space-y-3" variants={itemVariants} data-id="element-1394">
        {features.map((feature, index) => <motion.div key={index} className={`flex items-center text-left bg-white/5 p-4 border ${feature.border} hover:bg-white/10 transition-colors group relative overflow-hidden`} variants={itemVariants} data-id="element-1395">
            <div className={`p-2 mr-3 ${feature.bg} border ${feature.border}`} data-id="element-1396">
              <feature.icon size={18} className={feature.color} strokeWidth={2} data-id="element-1397" />
            </div>
            <div className="flex-1" data-id="element-1398">
              <h4 className="font-bold text-sm mb-0.5 text-white tracking-wide" data-id="element-1399">
                {feature.title}
              </h4>
              <p className="text-gray-500 text-xs font-mono" data-id="element-1400">
                {feature.description}
              </p>
            </div>
          </motion.div>)}
      </motion.div>

      {/* Scanner Widget */}
      <motion.div className="relative mb-8 group" variants={itemVariants} data-id="element-1401">
        <div className="relative bg-[#0a0a0a] border border-[#FF0000]/30 overflow-hidden group-hover:border-[#FF0000]/50 transition-colors duration-300" data-id="element-1402">
          {/* Corner Markers */}
          <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-[#FF0000] z-20" data-id="element-1403" />
          <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-[#FF0000] z-20" data-id="element-1404" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-[#FF0000] z-20" data-id="element-1405" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-[#FF0000] z-20" data-id="element-1406" />

          <div className="p-8 text-center relative overflow-hidden" data-id="element-1407">
            {/* SCANNING LINE ANIMATION */}
            <motion.div className="absolute top-0 left-0 w-full h-[2px] bg-[#FF0000] shadow-[0_0_20px_#FF0000] z-10" animate={{
            top: ['0%', '100%', '0%']
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-1408" />

            {/* Grid Overlay */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,0,0,0.03)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" data-id="element-1409" />

            <input type="file" id="onboarding-file-upload" className="hidden" onChange={handleFileChange} accept="image/*,.pdf" data-id="element-1410" />
            <label htmlFor="onboarding-file-upload" className="cursor-pointer block relative z-20" data-id="element-1411">
              <motion.div whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="inline-flex items-center justify-center w-16 h-16 bg-[#FF0000]/10 mb-4 border border-[#FF0000]/30 shadow-[0_0_20px_rgba(255,0,0,0.1)] group-hover:bg-[#FF0000]/20 transition-colors" data-id="element-1412">
                <Upload className="h-7 w-7 text-[#FF0000]" strokeWidth={2} data-id="element-1413" />
              </motion.div>

              <p className="font-bold text-white text-base mb-2 tracking-wide" data-id="element-1414">
                Drop Evidence Here
              </p>

              {selectedFile ? <div className="inline-flex items-center gap-2 bg-[#FF0000]/20 border border-[#FF0000]/50 px-3 py-1.5 text-[#FF0000] font-mono text-xs" data-id="element-1415">
                  <CheckCircle2 size={12} data-id="element-1416" />
                  <span data-id="element-1417">LOADED: {selectedFile.name}</span>
                </div> : <div className="inline-flex items-center gap-2 text-[10px] text-gray-500 font-mono border border-white/10 px-3 py-1.5 bg-black/50" data-id="element-1418">
                  <Lock size={10} data-id="element-1419" />
                  <span data-id="element-1420">ENCRYPTED UPLOAD</span>
                </div>}
            </label>
          </div>
        </div>
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-1421">
        {/* Payment Buttons */}
        <PaystackPaymentButtons onSuccess={onUnlock} data-id="element-1422" />

        {/* Skip to Dashboard */}
        <motion.button onClick={onUnlock} className="w-full mt-4 text-gray-500 text-xs font-mono uppercase tracking-widest hover:text-white transition-colors duration-200 flex items-center justify-center gap-2 group py-3" whileHover={{
        scale: 1.01
      }} data-id="element-1423">
          Skip to Dashboard
          <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" data-id="element-1424" />
        </motion.button>
      </motion.div>

      <motion.div className="mt-6 text-center" variants={itemVariants} data-id="element-1425">
        <p className="text-[10px] text-gray-600 font-mono uppercase tracking-widest" data-id="element-1426">
          🔒 Secure Payment • Encrypted Session
        </p>
      </motion.div>
    </motion.div>;
};